import React, { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import useEmblaCarousel from "embla-carousel-react";
import { ChevronLeft, ChevronRight, Search as SearchIcon, Filter } from "lucide-react";
import { api } from "@/lib/api";
import { FeaturedCard, ArticleRow, SectionTitle } from "@/components/ArticleCards";
import { ARTICLE_TYPES } from "@/lib/constants";
import { useI18n } from "@/lib/i18n";

export default function Home() {
  const { t } = useI18n();
  const [articles, setArticles] = useState([]);
  const [featured, setFeatured] = useState([]);
  const [loading, setLoading] = useState(true);
  const [emblaRef, emblaApi] = useEmblaCarousel({ slidesToScroll: 1, align: "start", loop: false });
  const [snaps, setSnaps] = useState([]);
  const [selected, setSelected] = useState(0);
  const [params, setParams] = useSearchParams();

  // Local filter UI state
  const [q, setQ] = useState(params.get("q") || "");
  const [type, setType] = useState(params.get("type") || "");
  const [year, setYear] = useState(params.get("year") || "");
  const [vol, setVol] = useState(params.get("vol") || "");
  const [issue, setIssue] = useState(params.get("issue") || "");

  useEffect(() => {
    setLoading(true);
    api.get("/articles", { params: { featured: true, limit: 12 } }).then((r) => {
      setFeatured(r.data || []);
    }).catch(() => {});
    api.get("/articles", { params: { limit: 100 } }).then((r) => {
      setArticles(r.data || []);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (!emblaApi) return;
    setSnaps(emblaApi.scrollSnapList());
    emblaApi.on("select", () => setSelected(emblaApi.selectedScrollSnap()));
  }, [emblaApi, articles]);

  // Carousel: the 6 most recent accepted/published articles (list is sorted newest-first).
  const carouselSource = articles.slice(0, 6);

  const filtered = useMemo(() => {
    return articles.filter((a) => {
      if (q && !(`${a.title} ${a.abstract} ${(a.keywords || []).join(" ")}`.toLowerCase().includes(q.toLowerCase()))) return false;
      if (type && a.article_type !== type) return false;
      if (year && a.year !== year) return false;
      if (vol && a.volume !== vol) return false;
      if (issue && a.issue !== issue) return false;
      return true;
    });
  }, [articles, q, type, year, vol, issue]);

  const visibleArticles = filtered.slice(0, 8);

  const years = useMemo(() => Array.from(new Set(articles.map((a) => a.year).filter(Boolean))).sort((a, b) => b.localeCompare(a)), [articles]);
  const volumes = useMemo(() => Array.from(new Set(articles.map((a) => a.volume).filter(Boolean))).sort(), [articles]);
  const issues = useMemo(() => Array.from(new Set(articles.map((a) => a.issue).filter(Boolean))).sort(), [articles]);

  return (
    <div data-testid="home-page">
      {/* ------- Journal masthead / identity ------- */}
      <section className="bg-[#001F3F] py-12 md:py-16" data-testid="home-hero">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10">
          <h1 className="font-serif text-white text-3xl sm:text-4xl lg:text-5xl leading-tight">
            Clinical &amp; Surgical Review
          </h1>
          <div className="mt-3 h-[3px] w-20 bg-[#2BA6A6]" />
          <p className="mt-4 font-sans text-[#B9C6D6] text-base md:text-lg tracking-wide">
            {t("home.hero_subtitle")}
          </p>
        </div>
      </section>

      {/* ------- Featured / Recent Articles Carousel ------- */}
      <section className="bg-[#F5F7FA] py-14 md:py-20" data-testid="featured-section">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 relative">
          {carouselSource.length === 0 ? (
            <div className="text-center py-16 font-serif text-[#001F3F]/60 text-lg">
              {loading ? t("home.loading") : t("home.empty_carousel")}
            </div>
          ) : (
            <>
              <div className="overflow-hidden" ref={emblaRef}>
                <div className="flex gap-6">
                  {carouselSource.map((art) => (
                    <div key={art.id} className="min-w-0 flex-[0_0_100%] md:flex-[0_0_calc(33.333%-1rem)]">
                      <FeaturedCard article={art} />
                    </div>
                  ))}
                </div>
              </div>
              <button
                onClick={() => emblaApi?.scrollPrev()}
                className="hidden md:flex absolute left-2 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-white shadow-md items-center justify-center text-[#001F3F] hover:bg-[#001F3F] hover:text-white transition-colors"
                aria-label="Previous"
                data-testid="carousel-prev"
              >
                <ChevronLeft className="w-6 h-6" />
              </button>
              <button
                onClick={() => emblaApi?.scrollNext()}
                className="hidden md:flex absolute right-2 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-white shadow-md items-center justify-center text-[#001F3F] hover:bg-[#001F3F] hover:text-white transition-colors"
                aria-label="Next"
                data-testid="carousel-next"
              >
                <ChevronRight className="w-6 h-6" />
              </button>
              {snaps.length > 1 && (
                <div className="flex justify-center gap-2 mt-8" data-testid="carousel-dots">
                  {snaps.map((_, i) => (
                    <button
                      key={i}
                      onClick={() => emblaApi?.scrollTo(i)}
                      aria-label={`Slide ${i + 1}`}
                      className={`w-2.5 h-2.5 rounded-full transition-colors ${
                        selected === i ? "bg-[#2BA6A6]" : "bg-gray-300"
                      }`}
                    />
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      </section>

      {/* ------- Published Articles section ------- */}
      <section className="py-16 md:py-24" data-testid="published-articles-section">
        <div className="max-w-3xl mx-auto px-6">
          <SectionTitle subtitle={t("home.published_subtitle")}>
            {t("home.published_articles")}
          </SectionTitle>

          {/* Filters */}
          <div className="bg-[#F5F7FA] border border-gray-200 p-5 mb-8" data-testid="article-filters">
            <div className="flex items-center gap-2 text-[#001F3F] mb-4">
              <Filter className="w-4 h-4 text-[#2BA6A6]" />
              <span className="font-sans text-sm uppercase tracking-wider font-semibold">{t("home.filter_title")}</span>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-12 gap-3">
              <div className="md:col-span-5 relative">
                <SearchIcon className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  placeholder={t("home.filter_search_placeholder")}
                  className="w-full pl-10 pr-3 py-2.5 bg-white border border-gray-200 text-sm font-sans focus:outline-none focus:border-[#2BA6A6]"
                  data-testid="filter-search"
                />
              </div>
              <select value={type} onChange={(e) => setType(e.target.value)} className="md:col-span-3 px-3 py-2.5 bg-white border border-gray-200 text-sm font-sans focus:outline-none focus:border-[#2BA6A6]" data-testid="filter-type">
                <option value="">{t("home.filter_all_types")}</option>
                {ARTICLE_TYPES.map((typ) => <option key={typ} value={typ}>{typ}</option>)}
              </select>
              <select value={year} onChange={(e) => setYear(e.target.value)} className="md:col-span-2 px-3 py-2.5 bg-white border border-gray-200 text-sm font-sans focus:outline-none focus:border-[#2BA6A6]" data-testid="filter-year">
                <option value="">{t("home.filter_all_years")}</option>
                {years.map((y) => <option key={y} value={y}>{y}</option>)}
              </select>
              <select value={vol} onChange={(e) => setVol(e.target.value)} className="md:col-span-1 px-3 py-2.5 bg-white border border-gray-200 text-sm font-sans focus:outline-none focus:border-[#2BA6A6]" data-testid="filter-volume">
                <option value="">{t("home.filter_vol_short")}</option>
                {volumes.map((v) => <option key={v} value={v}>{v}</option>)}
              </select>
              <select value={issue} onChange={(e) => setIssue(e.target.value)} className="md:col-span-1 px-3 py-2.5 bg-white border border-gray-200 text-sm font-sans focus:outline-none focus:border-[#2BA6A6]" data-testid="filter-issue">
                <option value="">{t("home.filter_iss_short")}</option>
                {issues.map((v) => <option key={v} value={v}>{v}</option>)}
              </select>
            </div>
          </div>

          {/* List */}
          <div data-testid="article-list">
            {visibleArticles.length === 0 ? (
              <div className="text-center py-20 font-serif text-[#001F3F]/60 text-lg border border-dashed border-gray-200">
                {loading ? t("home.loading") : t("home.empty_list")}
              </div>
            ) : (
              visibleArticles.map((a) => <ArticleRow key={a.id} article={a} />)
            )}
          </div>

          {filtered.length > 0 && (
            <div className="text-center mt-12">
              <Link
                to="/articles"
                data-testid="view-all-articles-btn"
                className="inline-flex items-center gap-3 bg-[#001F3F] text-white px-8 py-4 text-sm font-sans uppercase tracking-[0.18em] font-semibold hover:bg-[#2BA6A6] transition-colors"
              >
                {t("home.view_all")}
                <ChevronRight className="w-4 h-4" />
              </Link>
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
